// @flow
import {ROUTE} from 'app/constants/route';
import cn from 'classnames';
import {logOut} from 'modules/auth/actions/auth';
import {selectIsStudent, selectUserData} from 'modules/auth/selectors/auth';
import {HeaderNotificationContainer} from 'modules/common/components/HeaderNotificationContainer';
import {useSwitchState} from 'modules/common/hooks/use-switch-state';
import {ReactComponent as PersonIcon} from 'modules/common/icons/person.svg';
// import {ReactComponent as RingIcon} from 'modules/common/icons/ring.svg';
import SiteLogo from 'modules/common/images/logo.png';
import type {TNotification} from 'modules/common/types';
import React, {useCallback} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {useKeyPressEvent} from 'react-use';
import styles from './styles.local.less';

type TProps = {
    notificationList?: TNotification[],
    onShow?: () => void,
    showMenu?: boolean,
};
/**
 * Шапка сайта
 * @returns React-компонент
 */
export const Header = ({notificationList, onShow, showMenu}: TProps) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const [showNotifications, {toggle: toggleNotifications}] = useSwitchState(false);
    const [showProfile, {toggle: toggleProfile}] = useSwitchState(false);
    const isStudent = useSelector(selectIsStudent);
    const {id, email, firstName, companyName, companyUrl} = useSelector(selectUserData);
    // TODO: доработать
    /* const isExistNewMessage = useMemo(() => Boolean(notificationList && notificationList.find((notification) => notification.isNew)), [
        notificationList,
    ]);*/

    const NEW_EMAIL_KEY = `__new-email_${id}`;
    let newEmail;
    const newEmailInfo = window.localStorage.getItem(NEW_EMAIL_KEY);
    if (newEmailInfo) {
        if (1000 * 3600 * 24 > Math.abs(new Date().getTime() - JSON.parse(newEmailInfo).timeStamp)) {
            newEmail = JSON.parse(newEmailInfo).newEmail;
        } else {
            window.localStorage.removeItem(NEW_EMAIL_KEY);
        }
    }

    useKeyPressEvent('Escape', () => {
        if (showMenu) {
            if (onShow) {
                onShow();
            }
        }
        if (showNotifications) {
            toggleNotifications();
        }
        if (showProfile) {
            toggleProfile();
        }
    });

    const onLogOut = useCallback(() => {
        dispatch(logOut());
    }, [dispatch]);

    return (
        <header className={styles.header}>
            <div className={styles.leftSide}>
                <button
                    className={cn(styles.headerMenu, {[styles.headerMenuWhenMenuIsOpen]: showMenu}, {[styles.headerMenuIsStudent]: isStudent})}
                    onClick={onShow}
                >
                    <span className={styles.headerMenuInner}>
                        <div className={styles.headerMenuItem} />
                        <div className={styles.headerMenuItem} />
                        <div className={styles.headerMenuItem} />
                    </span>
                </button>
                <Link className={styles.headerLogo} to={isStudent ? `/${ROUTE.TRAINING_MODULE}` : `/${ROUTE.DASHBOARD}`}>
                    <img alt="logo" src={SiteLogo} />
                </Link>
                {companyUrl && <img alt="Company logo" className={styles.headerCompanyLogo} src={companyUrl} />}
                <div className={cn('cropping-to-points', styles.headerCompanyName)} title={companyName}>
                    {companyName}
                </div>
            </div>
            <div className={styles.rightSide}>
                {/* <button className={styles.headerNotifications} onClick={toggleNotifications}>
                    <div className={cn(styles.buttonOpenNotificationsPopup, {[styles.newMessage]: isExistNewMessage})}>
                        <RingIcon />
                    </div>
                </button>*/}
                <button className={cn(styles.headerProfile, {[styles.showProfilePopup]: showProfile})} onClick={toggleProfile}>
                    <div className={styles.wrapperCloseProfilePopup} onClick={toggleProfile} role="presentation" />
                    <div className={styles.buttonOpenProfilePopup}>
                        <PersonIcon />
                    </div>
                    <div className={styles.profilePopup}>
                        <div className={cn('cropping-to-points', styles.profilePopupName)} title={firstName}>
                            {firstName}
                        </div>
                        <div className={cn('cropping-to-points', styles.profilePopupEmail)} title={newEmail ? newEmail : email}>
                            {newEmail ? newEmail : email}
                        </div>
                        <Link className={cn('f-w_regular', styles.profilePopupLink)} to={`/${ROUTE.SETTINGS}/profile`}>
                            {intl.formatMessage({defaultMessage: 'Настройки аккаунта', id: 'common.header.linkToProfile'})}
                        </Link>
                        {!isStudent && (
                            <Link className={cn('f-w_regular', styles.profilePopupLink)} to={`/${ROUTE.SETTINGS}/company`}>
                                {intl.formatMessage({defaultMessage: 'Настройки компании', id: 'common.header.linkToCompany'})}
                            </Link>
                        )}
                        <a
                            className={cn('f-w_regular', styles.profilePopupLink)}
                            href="https://support.kaspersky.com/CITO/Launch/en-US/201775.htm"
                            rel="noreferrer"
                            target="_blank"
                        >
                            {intl.formatMessage({defaultMessage: 'Online help', id: 'common.header.onlineHelp'})}
                        </a>
                        <a
                            className={cn('f-w_regular', styles.profilePopupLink)}
                            href="mailto:cito-support@kaspersky.com?subject=Kaspersky CITO support request"
                            rel="noreferrer"
                            target="_blank"
                        >
                            {intl.formatMessage({defaultMessage: 'Support contacts', id: 'common.header.supportContacts'})}
                        </a>
                        <Link className={cn('f-w_regular', styles.profilePopupLink)} to={`/${ROUTE.DOCUMENTS}`}>
                            {intl.formatMessage({defaultMessage: 'Terms & Polices', id: 'common.header.termsPolices'})}
                        </Link>
                        <span
                            className={cn('f-w_regular', styles.profilePopupLink)}
                            onClick={onLogOut}
                            onKeyDown={onLogOut}
                            role="button"
                            tabIndex={0}
                        >
                            {intl.formatMessage({defaultMessage: 'Выйти', id: 'settings.profileSettings.logout'})}
                        </span>
                    </div>
                </button>
            </div>
            {notificationList && (
                <HeaderNotificationContainer isOpen={showNotifications} notificationList={notificationList} onClose={toggleNotifications} />
            )}
        </header>
    );
};
